/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo, Suspense } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

// Material Dashboard 2 React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// Material Dashboard 2 React Dark Mode themes
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Material Dashboard 2 React routes
import routes from "routes";
import VerifyOtp from "layouts/authentication/verify-otp";

// Material Dashboard 2 React contexts
import { useMaterialUIController, setMiniSidenav, setOpenConfigurator } from "context";

// Images
import brandWhite from "assets/images/jobs_logo.png";
import brandDark from "assets/images/jobs_logo.png";
import AllApplicants from "layouts/jobs/all-applicants";
import ProfileDetail from "layouts/profile/profile-detail";
import SignUp from "layouts/authentication/sign-up";
import SignIn from "layouts/authentication/sign-in";
import AdminSignIn from "layouts/authentication/admin-sign-in";
import ResetPassword from "layouts/authentication/reset-password";
import ChangePassword from "layouts/authentication/change-password";
import ForgotPassword from "layouts/authentication/forgot-password";
import AdminProfile from "layouts/profile/admin-profile";
import AllJobs from "layouts/jobs/all-jobs";
import AllContacts from "layouts/company/all-contacts";
import CompanyDetail from "layouts/profile/company-detail";
import { CircularProgress } from "@mui/material";
import JobDetails from "layouts/jobs/job-details";
import DeleteAccount from "layouts/profile/delete-account";
import VerifyOtpDeleteAccount from "layouts/profile/verify-otp";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      // console.log("_______route________", route);
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      }

      return null;
    });

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  return (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {layout === "dashboard" && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
            // brandName="JITO JOBS VADODARA"
            routes={routes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
          <Configurator />
          {/* {configsButton} */}
        </>
      )}
      {layout === "vr" && <Configurator />}
      <Suspense
        fallback={
          <MDBox display="flex" justifyContent="center" mt={2}>
            <CircularProgress />
          </MDBox>
        }
      >
        <Routes>
          {getRoutes(routes)}
          {/* <Route exact path="/authentication/verify-otp" element={<VerifyOtp />} />
          <Route exact path="/authentication/sign-in" element={<SignIn />} />
          <Route exact path="/authentication/sign-up" element={<SignUp />} /> */}
          <Route path="/authentication/admin-sign-in" element={<AdminSignIn />} />
          <Route path="/authentication/reset-password" element={<ResetPassword />} />
          <Route path="/authentication/change-password" element={<ChangePassword />} />
          <Route path="/authentication/forgot-password" element={<ForgotPassword />} />

          <Route path="/profile/admin-profile" element={<AdminProfile />} />

          <Route path="/jobs/all-jobs" element={<AllJobs />} />

          <Route path="/jobs/all-applicants" element={<AllApplicants />} />
          <Route path="/profile/profile-detail" element={<ProfileDetail />} />
          <Route path="/profile/company-detail" element={<CompanyDetail />} />

          <Route path="/company/all-contacts" element={<AllContacts />} />

          <Route path="/profile/delete-account" element={<DeleteAccount />} />
          <Route path="/profile/verify-otp" element={<VerifyOtpDeleteAccount />} />

          <Route path="/jobs/job-details" element={<JobDetails />} />

          <Route path="*" element={<Navigate to="/authentication/admin-sign-in" />} />
        </Routes>
      </Suspense>

      <ToastContainer position="top-right" autoClose={1000} />
    </ThemeProvider>
  );
}
